import { ApolloError } from '@apollo/client';
import { calculateItemsTotal, findDeletedItemsById, zeroOutQty } from 'common/PriceTotalsUtils';
import { isDefined } from 'common/TypeUtilities';
import { debounce, first, groupBy, sum } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useOrderCart } from 'src/cart/hooks/useOrderCart';
import {
  EntireOrderTotalFragment,
  useGroupOrderTotalsLazyQuery,
  useModifiedGroupOrderTotalsLazyQuery,
} from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { useDispatch, useSelector } from 'src/store';
import { setShefTip } from 'src/store/cart/actions';
import {
  selectAddOnItems,
  selectCart,
  selectCartFoodItem,
  selectCartShefDeliveryDate,
  selectLineItemsSubtotalsByShefIds,
} from 'src/store/cart/selectors';
import { TippingStyle } from 'src/store/cart/types';
import { selectCheckoutOrdersInputs } from 'src/store/checkout';
import { IOrderTotals } from 'src/types/GenericTypes';
import { IDeliveryDetails } from './IDeliveryDetails';

interface IUseGroupOrderTotals {
  isEditingOrder?: boolean;
  isPickupOrder?: boolean;
  orderDetails?: IDeliveryDetails;
}

const mapModifiedCartLineItems = <T extends { foodItemId: number; quantity: number; price: number; shefId: string }>(
  lineItems: T[]
) => {
  const shefOrders = groupBy(lineItems, 'shefId');
  return Object.keys(shefOrders).map((shefId) => {
    const shefLineItems = shefOrders[shefId];
    return {
      shefId,
      lineItemsSubtotal: calculateItemsTotal(shefLineItems),
      lineItems: shefLineItems.map((item) => ({
        foodItemId: item.foodItemId,
        quantity: item.quantity,
      })),
    };
  });
};

const mapCartAddOnItems = <T extends { id: number; quantity: number }>(addOnItems: T[]) =>
  addOnItems.map((item) => ({
    addOnItemId: item.id,
    quantity: item.quantity,
  }));

export function useGroupOrderTotals(
  options?: IUseGroupOrderTotals
): [IOrderTotals | null, boolean, ApolloError | null] {
  const isEditingOrder = Boolean(options?.isEditingOrder);
  const isPickupOrder = Boolean(options?.isPickupOrder);
  const { deliveryAddress, deliveryUrlDate, recurringOrderPeriod } = options?.orderDetails ?? {};

  const dispatch = useDispatch();
  const { cart, addOnItemsTotal, addOnItems, shefTip, shefTipPercent, tippingStyle, lineItemSubtotals, perShefOrders } =
    useSelector((state) => {
      const cart = selectCart(state, isEditingOrder);
      const addOnItems = selectAddOnItems(cart);
      const perShefOrders = selectCheckoutOrdersInputs(state, cart);
      const shefIds = perShefOrders.map((order) => order.shefId);
      const firstShefId = first(shefIds) || '';
      // select delivery date from first shef here, this allows us to maintain single shef
      // carts, as their delivery date can be different than root level cart's delivery date
      const deliveryDate = selectCartShefDeliveryDate(cart, firstShefId) || cart.deliveryDate;

      return {
        cart,
        addOnItemsTotal: calculateItemsTotal(addOnItems),
        shefTip: cart.shefTip,
        shefTipPercent: cart.shefTipPercent,
        tippingStyle: cart.tippingStyle,
        addOnItems,
        deliveryDate,
        lineItemSubtotals: selectLineItemsSubtotalsByShefIds(cart, shefIds),
        perShefOrders,
      };
    });

  const addOnItemsStringified = JSON.stringify(addOnItems);

  // NOTE: using 'useSelector' to get lineItems causes a re-render loop causing the query bellow to run repeatedly,
  // this is a workaround
  const lineItems = useMemo(() => Object.values(cart.lineItems.entities).filter(isDefined), [cart]);

  const lineItemsSubtotal = useMemo(
    () => sum(perShefOrders.map((order) => lineItemSubtotals[order.shefId] ?? 0)),
    [perShefOrders, lineItemSubtotals]
  );

  // TODO: seems like we need a useGroupOrderCart, noOrders check seems wrong way to check but probs works
  const { order: originalOrderData } = useOrderCart({});
  const { groupOrderId, timeWindowId, orderType } = originalOrderData ?? {};
  const noOrders = ((isEditingOrder ? originalOrderData?.lineItems.length : perShefOrders.length) ?? 0) === 0;

  const [runGroupQuery, { data: groupCartData, error: groupCartQueryError, loading: groupCartQueryLoading }] =
    useGroupOrderTotalsLazyQuery({
      fetchPolicy: 'network-only',
    });
  const runGroupOrderTotalsQueryDebounce = useMemo(() => debounce(runGroupQuery, 500), [runGroupQuery]);

  const [runGroupOrderQuery, { data: groupOrderData, error: groupOrderQueryError, loading: groupOrderQueryLoading }] =
    useModifiedGroupOrderTotalsLazyQuery({
      fetchPolicy: 'network-only',
    });
  const runModifiedGroupOrderTotalsQueryDebounce = useMemo(
    () => debounce(runGroupOrderQuery, 500),
    [runGroupOrderQuery]
  );

  const queryError = (isEditingOrder ? groupOrderQueryError : groupCartQueryError) ?? null;
  const queryLoading = isEditingOrder ? groupOrderQueryLoading : groupCartQueryLoading;
  const groupOrderTotals: EntireOrderTotalFragment | null =
    (isEditingOrder ? groupOrderData?.modifiedGroupOrderTotals : groupCartData?.groupOrderTotals) ?? null;

  const { code: promoCode } = usePromotionalsHook();

  useEffect(() => {
    if (isEditingOrder && groupOrderId && !noOrders) {
      const foodItemOriginal =
        originalOrderData?.lineItems.map(({ foodItemId, quantity }) => ({
          foodItemId,
          quantity,
          shefId: originalOrderData.shefId,
        })) ?? [];

      const addOnItemOriginal =
        originalOrderData?.addOnItems.map((item) => ({
          id: item.addOnItemId,
          quantity: item.quantity,
          price: item.addOnItem.price,
        })) ?? [];

      const newLineItems = lineItems.map(({ foodItemId, quantity, shefId }) => ({
        foodItemId,
        quantity,
        shefId,
      }));
      const newAddonItems = addOnItems.map(({ id, quantity, price }) => ({ id, quantity, price }));

      const deletedFoodItems = findDeletedItemsById(foodItemOriginal, newLineItems, 'foodItemId');
      const foodItemsThatNeedZeroQty = zeroOutQty(deletedFoodItems);

      const deletedAddonItems = findDeletedItemsById(addOnItemOriginal, newAddonItems, 'id');
      const addOnItemsThatNeedZeroQty = zeroOutQty(deletedAddonItems);

      const adjustedLineItems = [...newLineItems, ...foodItemsThatNeedZeroQty].map(
        ({ foodItemId, quantity, shefId }) => ({
          foodItemId,
          quantity,
          price: selectCartFoodItem(cart, foodItemId)?.price ?? 0,
          shefId,
        })
      );
      const adjustedAddOnItems = [...newAddonItems, ...addOnItemsThatNeedZeroQty].map(({ id, quantity, price }) => ({
        id,
        quantity,
        price,
      }));
      runModifiedGroupOrderTotalsQueryDebounce({
        variables: {
          input: {
            id: groupOrderId,
            timeWindowId,
            deliveryAddressId: originalOrderData?.deliveryAddressId,
            deliveryAddress,
            deliveryNotes: originalOrderData?.deliveryNotes,
            phone: originalOrderData?.phone,
            deliveryDate: deliveryUrlDate,
            perShefOrders: mapModifiedCartLineItems(adjustedLineItems),
            addOnItems: mapCartAddOnItems(adjustedAddOnItems),
            addOnItemsSubtotal: calculateItemsTotal(adjustedLineItems),
            shefTip: shefTip ?? 0,
            replaceRecurring: false, // TODO Once recurring orders are on dish-first, this needs to be calculated
          },
        },
      });
    }
  }, [
    cart,
    deliveryAddress,
    deliveryUrlDate,
    groupOrderId,
    isEditingOrder,
    lineItems,
    originalOrderData?.addOnItems,
    originalOrderData?.deliveryAddressId,
    originalOrderData?.deliveryNotes,
    originalOrderData?.lineItems,
    originalOrderData?.phone,
    runModifiedGroupOrderTotalsQueryDebounce,
    shefTip,
    timeWindowId,
    addOnItemsStringified,
  ]);

  useEffect(() => {
    if (!isEditingOrder && perShefOrders && (isPickupOrder || deliveryAddress) && !noOrders) {
      runGroupOrderTotalsQueryDebounce({
        variables: {
          groupOrderInput: {
            perShefOrders,
            addOnItems: mapCartAddOnItems(addOnItems),
            lineItemsSubtotal,
            addOnItemsSubtotal: addOnItemsTotal,
            shefTip: shefTip ?? 0,
            shefTipPercent,
            expectedAdditionalDeliveryDateSpend: 0, // TODO: remove field
            deliveryDate: deliveryUrlDate,
            deliveryAddress,
            promoCode: promoCode?.code ?? null,
            orderType,
            recurringPeriod: recurringOrderPeriod,
          },
        },
      });
    }
  }, [
    addOnItemsStringified,
    addOnItemsTotal,
    deliveryAddress,
    deliveryUrlDate,
    groupOrderTotals,
    isEditingOrder,
    isPickupOrder,
    orderType,
    promoCode?.code,
    recurringOrderPeriod,
    runGroupOrderTotalsQueryDebounce,
    shefTip,
    shefTipPercent,
    perShefOrders,
    groupOrderQueryError,
  ]);

  useEffect(() => {
    if (tippingStyle === TippingStyle.PERCENTAGE && groupOrderTotals) {
      dispatch(setShefTip(isEditingOrder, groupOrderTotals?.shefTip ?? 0));
    }
  }, [isEditingOrder, tippingStyle, groupOrderTotals, dispatch]);

  if (noOrders) {
    console.error('Received no orders to calculate totals on.');
    return [null, false, null];
  }

  return [groupOrderTotals, queryLoading, queryError];
}
